import { useState } from "react";
import {
  useListEcoleEleves,
  useCreateEcoleEleve,
  useUpdateEcoleEleve,
  useGenererCode,
  useDoublons,
  useClassesEcole,
} from "@/lib/backoffice-hooks";
import { useAnneeScope } from "@/lib/annee-scope";
import EcoleShell from "@/components/layout/EcoleShell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Loader2, ChevronRight, Pencil, KeyRound, AlertTriangle, Printer, Copy } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Link } from "wouter";
import CsvImportCard from "@/components/CsvImportCard";
import ClasseSelect from "@/components/ClasseSelect";
import AnneeInactiveBanner from "@/components/AnneeInactiveBanner";
import { PaginationControls } from "@/components/PaginationControls";
import { importElevesCsv, type EcoleEleve } from "@/lib/api";
import { downloadFicheRattachement, downloadFichesClasse } from "@/lib/downloadRecu";
import { friendlyError } from "@/lib/errors";

interface EditEleveState {
  id: string;
  nom: string;
  prenom: string;
  dateNaissance: string;
  sexe: "" | "M" | "F";
  matricule: string;
  /** Id de classe sélectionné ("" si l'élève n'en a pas / classe hors année). */
  classe: string;
  /** Id résolu à l'ouverture — n'envoyer `classe` que s'il a changé. */
  classeInitiale: string;
}

export default function ElevesPage() {
  const { isActiveAnnee } = useAnneeScope();
  const { data: eleves, isLoading } = useListEcoleEleves();
  const { data: classes } = useClassesEcole();
  const updateMutation = useUpdateEcoleEleve();
  const genererCode = useGenererCode();
  const { data: doublons } = useDoublons();
  const [open, setOpen] = useState(false);
  const [editEleve, setEditEleve] = useState<EditEleveState | null>(null);
  const [search, setSearch] = useState("");
  // Code de rattachement + jeton QR générés par élève (id → {code, qrToken}).
  const [codes, setCodes] = useState<Record<string, { code: string; qrToken?: string }>>({});
  // Classe ciblée par l'impression de la planche de fiches de rattachement.
  const [ficheClasseId, setFicheClasseId] = useState("");
  // Pagination d'affichage du registre (0-based, comme PaginationControls).
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  function handleGenererCode(eleveId: string) {
    genererCode.mutate(eleveId, {
      onSuccess: (res) => {
        setCodes((c) => ({ ...c, [eleveId]: { code: res.code, qrToken: res.qrToken } }));
        toast.success("Code de rattachement généré.");
      },
      onError: (err: unknown) => toast.error(friendlyError(err)),
    });
  }

  function openEdit(e: EcoleEleve) {
    // Le roster ne porte que le LIBELLÉ de classe : on le résout vers l'id
    // pour pré-sélectionner le Select ("" si aucune classe / hors année).
    const classeId = classes?.find((c) => c.libelle === e.classe)?.id ?? "";
    setEditEleve({
      id: e.id,
      nom: e.nom,
      prenom: e.prenoms,
      dateNaissance: "",
      sexe: e.sexe ?? "",
      matricule: e.matriculeInterne,
      classe: classeId,
      classeInitiale: classeId,
    });
  }

  function submitEdit() {
    /* v8 ignore start -- défensif : submitEdit n'est appelable que depuis le dialog rendu quand editEleve est non nul */
    if (!editEleve) return;
    /* v8 ignore stop */
    updateMutation.mutate(
      {
        eleveId: editEleve.id,
        data: {
          nom: editEleve.nom,
          prenom: editEleve.prenom,
          dateNaissance: editEleve.dateNaissance || undefined,
          sexe: editEleve.sexe || undefined,
          matricule: editEleve.matricule || undefined,
          // Changement de classe : seulement si une AUTRE classe est choisie
          // (le PATCH backend ignore blanc = pas de retrait de classe ici).
          classe:
            editEleve.classe && editEleve.classe !== editEleve.classeInitiale
              ? editEleve.classe
              : undefined,
        },
      },
      {
        onSuccess: () => {
          // L'invalidation du roster est gérée par useUpdateEcoleEleve.
          toast.success("Élève modifié");
          setEditEleve(null);
        },
        onError: (err: unknown) => toast.error(friendlyError(err)),
      },
    );
  }
  const filtered = (eleves ?? []).filter((e) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return `${e.prenoms} ${e.nom} ${e.matriculeInterne} ${e.classe ?? ""}`.toLowerCase().includes(q);
  });

  // Pagination CLIENT : l'effectif complet est déjà chargé (≤ 5000, borne
  // backend) et la recherche doit rester GLOBALE — on ne pagine que
  // l'affichage. La page revient à 1 quand la recherche ou la taille change.
  const nbPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, nbPages - 1);
  const pageRows = filtered.slice(safePage * pageSize, (safePage + 1) * pageSize);

  return (
    <EcoleShell>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-navy">Élèves</h1>
          <p className="text-sm text-slate-600">{eleves?.length ?? 0} enregistré(s)</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-navy hover:bg-navy/90 text-white" data-testid="button-create-eleve" disabled={!isActiveAnnee}>
              <Plus className="w-4 h-4 mr-1.5" /> Nouvel élève
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader className="-mx-6 -mt-6 rounded-t-lg bg-navy px-6 py-4 text-left">
              <DialogTitle className="font-display text-lg font-bold text-white">Enregistrer un nouvel élève</DialogTitle>
              <DialogDescription className="text-white/70">Ajoutez un élève au registre de votre école.</DialogDescription>
            </DialogHeader>
            <CreateEleveForm onDone={() => setOpen(false)} />
          </DialogContent>
        </Dialog>
      </div>

      {!isActiveAnnee && <AnneeInactiveBanner />}

      <Card className="border border-slate-200 shadow-none">
        <div className="p-3 border-b border-slate-200">
          <Input
            placeholder="Rechercher par nom, matricule, classe…"
            value={search}
            onChange={(e) => {
              // Nouvelle recherche → retour page 1 (sinon page hors bornes).
              setSearch(e.target.value);
              setPage(0);
            }}
            className="max-w-md"
            data-testid="input-search"
          />
        </div>
        {isLoading && <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-navy" /></div>}
        {!isLoading && (
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="text-left px-4 py-2.5">Matricule</th>
                <th className="text-left">Nom</th>
                <th className="text-left">Prénoms</th>
                <th className="text-left">Classe</th>
                <th className="text-left">Année</th>
                <th className="text-left">Matricule SEDEC officiel</th>
                <th className="pr-4"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-slate-500">Aucun élève.</td></tr>}
              {pageRows.map((e) => (
                <tr key={e.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" data-testid={`row-eleve-${e.matriculeInterne}`} onClick={() => { window.location.href = `/ecole/eleves/${e.id}`; }}>
                  <td className="px-4 py-2.5 font-mono text-xs text-navy" style={{ fontFamily: "JetBrains Mono, monospace" }}>{e.matriculeInterne}</td>
                  <td className="font-medium uppercase">{e.nom}</td>
                  <td>{e.prenoms}</td>
                  <td className="text-slate-600">{e.classe ?? "·"}</td>
                  <td className="text-xs text-slate-500">{e.anneeScolaire}</td>
                  <td className="text-xs text-slate-500 font-mono" style={{ fontFamily: "JetBrains Mono, monospace" }}>{e.matriculeSedecOfficiel ?? "·"}</td>
                  <td className="pr-4 text-right whitespace-nowrap" onClick={(ev) => ev.stopPropagation()}>
                    <button
                      onClick={() => openEdit(e)}
                      disabled={!isActiveAnnee}
                      className="text-xs px-2 py-1 mr-2 border border-slate-200 rounded hover:bg-slate-50 inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid={`button-edit-${e.matriculeInterne}`}
                    >
                      <Pencil className="w-3 h-3" /> Modifier
                    </button>
                    <button
                      onClick={() => handleGenererCode(e.id)}
                      disabled={genererCode.isPending}
                      className="text-xs px-2 py-1 mr-2 border border-slate-200 rounded hover:bg-slate-50 inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid={`button-code-${e.matriculeInterne}`}
                      title="Afficher le code de rattachement parent (réutilise le code actif, en crée un sinon)"
                    >
                      <KeyRound className="w-3 h-3" /> Code de rattachement
                    </button>
                    <button
                      onClick={() => downloadFicheRattachement(e.id, e.matriculeInterne)}
                      className="text-xs px-2 py-1 mr-2 border border-slate-200 rounded hover:bg-slate-50 inline-flex items-center gap-1"
                      data-testid={`button-fiche-${e.matriculeInterne}`}
                      title="Imprimer la fiche de rattachement (PDF : code + QR à remettre au parent)"
                    >
                      <Printer className="w-3 h-3" /> Fiche PDF
                    </button>
                    {codes[e.id] && (
                      <span className="inline-flex items-center gap-2 mr-2 align-middle">
                        <span
                          className="text-xs font-mono px-1.5 py-0.5 rounded bg-green-50 text-green-700"
                          style={{ fontFamily: "JetBrains Mono, monospace" }}
                          data-testid={`code-${e.matriculeInterne}`}
                        >
                          {codes[e.id]?.code}
                        </span>
                        <button
                          onClick={() => {
                            // Bouton rendu sous la garde codes[e.id] — le code existe.
                            navigator.clipboard?.writeText(codes[e.id]!.code);
                            toast.success("Code copié");
                          }}
                          className="text-slate-500 hover:text-navy p-0.5"
                          title="Copier le code"
                          data-testid={`button-copier-${e.matriculeInterne}`}
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                        {codes[e.id]?.qrToken && (
                          <span
                            className="inline-block bg-white p-1 rounded border border-slate-200"
                            title="QR de rattachement : à scanner dans l'app CATHO SCHOOL"
                            data-testid={`qr-${e.matriculeInterne}`}
                          >
                            <QRCodeSVG
                              value={JSON.stringify({ t: "cathoschool-rtc", q: codes[e.id]?.qrToken })}
                              size={72}
                              level="M"
                            />
                          </span>
                        )}
                      </span>
                    )}
                    <Link href={`/ecole/eleves/${e.id}`} className="text-xs text-navy hover:underline inline-flex items-center gap-1" data-testid={`link-eleve-${e.matriculeInterne}`}>
                      Fiche <ChevronRight className="w-3 h-3" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
        {!isLoading && (
          <PaginationControls
            page={safePage}
            size={pageSize}
            total={filtered.length}
            onPageChange={setPage}
            onSizeChange={(s) => {
              setPageSize(s);
              setPage(0);
            }}
            sizes={[25, 50, 100]}
          />
        )}
      </Card>

      {(doublons ?? []).length > 0 && (
        <Card className="mt-4 border border-amber-200 shadow-none">
          <div className="p-3 border-b border-amber-200 flex items-center gap-2 text-amber-700">
            <AlertTriangle className="w-4 h-4" />
            <h2 className="text-sm font-bold">Doublons suspects</h2>
          </div>
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-amber-50 text-xs uppercase tracking-wide text-amber-700">
              <tr>
                <th className="text-left px-4 py-2">Nom</th>
                <th className="text-left">Prénoms</th>
                <th className="text-left">Occurrences</th>
                <th className="text-left">Matricules</th>
              </tr>
            </thead>
            <tbody>
              {/* v8 ignore start -- défensif : ce bloc n'est rendu que si (doublons ?? []).length > 0 (garde L190) ; le repli `?? []` est donc inatteignable ici */}
              {(doublons ?? []).map((d, i) => (
                <tr key={`${d.nom}-${d.prenoms}-${i}`} className="border-t border-amber-100">
                  <td className="px-4 py-2 font-medium uppercase">{d.nom}</td>
                  <td className="py-2">{d.prenoms}</td>
                  <td className="py-2 text-slate-600">{d.count}</td>
                  <td className="py-2 font-mono text-xs text-slate-500" style={{ fontFamily: "JetBrains Mono, monospace" }}>
                    {d.matricules.join(", ")}
                  </td>
                </tr>
              ))}
              {/* v8 ignore stop */}
            </tbody>
          </table>
          </div>
          <p className="px-4 py-2 text-xs text-slate-500">
            Utilisez l'écran « Demandes » pour fusionner les doublons (action « Fusionner »).
          </p>
        </Card>
      )}

      <Card className="mt-4 border border-slate-200 shadow-none">
        <div className="p-4 flex flex-wrap items-end gap-3">
          <div className="min-w-56">
            <div className="text-sm font-bold text-navy mb-1">Fiches de rattachement par classe</div>
            <p className="text-xs text-slate-500 mb-2">
              Planche A4 à découper : une fiche (code + QR) par élève de la classe, à distribuer aux parents.
            </p>
            <ClasseSelect value={ficheClasseId} onChange={setFicheClasseId} testId="select-classe-fiches" />
          </div>
          <Button
            onClick={() =>
              downloadFichesClasse(
                ficheClasseId,
                /* v8 ignore next -- défensif : la classe sélectionnée vient de ce même cache ; il faudrait une éviction entre la sélection et le clic */
                classes?.find((c) => c.id === ficheClasseId)?.libelle ?? "classe",
              )
            }
            disabled={!ficheClasseId}
            className="bg-navy hover:bg-navy/90 text-white"
            data-testid="button-fiches-classe"
          >
            <Printer className="w-4 h-4 mr-1.5" /> Imprimer les fiches (PDF)
          </Button>
        </div>
      </Card>

      <CsvImportCard
        title="Importer des élèves"
        description="Fichier CSV avec en-tête: matricule,nom,prenoms,sexe,dateNaissance,classe,lieuNaissance,nationalite. La classe doit être le libellé EXACT d'une classe existante, sinon la ligne part en erreur"
        columns={["matricule", "nom", "prenoms", "sexe", "dateNaissance", "classe", "lieuNaissance", "nationalite"]}
        exampleCsv={`matricule,nom,prenoms,sexe,dateNaissance,classe,lieuNaissance,nationalite
SEDEC-2026-00001,KONE,Adama,M,2015-03-12,CM1,,Ivoirienne
SEDEC-2026-00002,YAO,Marie,F,2016-08-22,CE2,Abidjan,Ivoirienne`}
        importFn={importElevesCsv}
        filename="modele-eleves.csv"
        invalidateQueries={[["ecole", "eleves"]]}
        disabled={!isActiveAnnee}
      />

      <Dialog
        open={!!editEleve}
        onOpenChange={(o) => {
          /* v8 ignore start -- défensif : sans DialogTrigger, Radix n'appelle onOpenChange qu'avec o=false (fermeture) ; la branche o=true est inatteignable */
          if (!o) setEditEleve(null);
          /* v8 ignore stop */
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader className="-mx-6 -mt-6 rounded-t-lg bg-navy px-6 py-4 text-left">
            <DialogTitle className="font-display text-lg font-bold text-white">Modifier l'élève</DialogTitle>
            <DialogDescription className="text-white/70">Mettez à jour les informations de l'élève.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Nom</Label><Input value={editEleve?.nom ?? ""} onChange={(e) => setEditEleve((p) => p && { ...p, nom: e.target.value.toUpperCase() })} data-testid="edit-input-nom" /></div>
              <div><Label>Prénoms</Label><Input value={editEleve?.prenom ?? ""} onChange={(e) => setEditEleve((p) => p && { ...p, prenom: e.target.value })} data-testid="edit-input-prenoms" /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div><Label>Date de naissance</Label><Input type="date" value={editEleve?.dateNaissance ?? ""} onChange={(e) => setEditEleve((p) => p && { ...p, dateNaissance: e.target.value })} data-testid="edit-input-naissance" /></div>
              <div><Label>Matricule</Label><Input value={editEleve?.matricule ?? ""} onChange={(e) => setEditEleve((p) => p && { ...p, matricule: e.target.value })} data-testid="edit-input-matricule" /></div>
              <div>
                <Label>Sexe</Label>
                <select className="w-full border border-slate-300 rounded px-2 h-9 text-sm" value={editEleve?.sexe ?? ""} onChange={(e) => setEditEleve((p) => p && { ...p, sexe: e.target.value as "" | "M" | "F" })} data-testid="edit-select-sexe">
                  <option value="">Non renseigné</option><option value="M">M</option><option value="F">F</option>
                </select>
              </div>
            </div>
            <div>
              <Label>Classe</Label>
              <ClasseSelect
                value={editEleve?.classe ?? ""}
                onChange={(id) => setEditEleve((p) => p && { ...p, classe: id })}
                testId="edit-select-classe"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setEditEleve(null)}>Annuler</Button>
              <Button onClick={submitEdit} disabled={updateMutation.isPending} className="bg-navy text-white hover:bg-navy/90" data-testid="button-submit-edit">
                {updateMutation.isPending ? "Enregistrement…" : "Enregistrer"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </EcoleShell>
  );
}

function CreateEleveForm({ onDone }: { onDone: () => void }) {
  const mutation = useCreateEcoleEleve();
  const { annees, anneeId } = useAnneeScope();
  const annee = annees.find((a) => a.id === anneeId);
  const [form, setForm] = useState({
    nom: "", prenom: "", dateNaissance: "", classe: "",
    lieuNaissance: "", sexe: "" as "" | "M" | "F",
  });
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        // Le backend exige nom, prenom (singulier) ET dateNaissance (@NotNull).
        if (!form.nom || !form.prenom || !form.dateNaissance) {
          toast.error("Nom, prénoms et date de naissance requis");
          return;
        }
        mutation.mutate({ data: {
          nom: form.nom,
          prenom: form.prenom,
          dateNaissance: form.dateNaissance,
          classe: form.classe || undefined,
          lieuNaissance: form.lieuNaissance || undefined,
          sexe: form.sexe || undefined,
        }}, {
          onSuccess: () => { toast.success("Élève enregistré"); onDone(); },
          onError: (err: unknown) => toast.error(friendlyError(err)),
        });
      }}
      className="space-y-3"
    >
        <div className="rounded-lg bg-cream px-3 py-2 text-xs text-navy/70">
          Année scolaire : <span className="font-bold">{annee?.libelle ?? "·"}</span>
        </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label>Nom</Label><Input value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value.toUpperCase() })} data-testid="input-nom" /></div>
        <div><Label>Prénoms</Label><Input value={form.prenom} onChange={(e) => setForm({ ...form, prenom: e.target.value })} data-testid="input-prenoms" /></div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div><Label>Date de naissance *</Label><Input type="date" value={form.dateNaissance} onChange={(e) => setForm({ ...form, dateNaissance: e.target.value })} data-testid="input-naissance" /></div>
        <div>
          <Label>Classe</Label>
          <ClasseSelect value={form.classe} onChange={(id) => setForm({ ...form, classe: id })} testId="input-classe" />
        </div>
        <div>
          <Label>Sexe</Label>
          <select className="w-full border border-slate-300 rounded px-2 h-9 text-sm" value={form.sexe} onChange={(e) => setForm({ ...form, sexe: e.target.value as any })} data-testid="input-sexe">
            <option value="">Non renseigné</option><option value="M">M</option><option value="F">F</option>
          </select>
        </div>
      </div>
      <div>
        <Label>Lieu de naissance (optionnel)</Label>
        <Input value={form.lieuNaissance} onChange={(e) => setForm({ ...form, lieuNaissance: e.target.value })} />
      </div>
      <Button type="submit" disabled={mutation.isPending} className="w-full bg-navy hover:bg-navy/90 text-white">
        {mutation.isPending ? "Enregistrement…" : "Enregistrer l'élève"}
      </Button>
    </form>
  );
}
